import React from 'react';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ChevronDown, 
  ChevronRight, 
  Shield,
  Eye,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  FileText
} from 'lucide-react';
import { Menu } from '../types/user';
import { RoleMenuPermission } from '../services/roleApi';

interface MenuPermissionsProps {
  menus: Menu[];
  permissions: RoleMenuPermission[];
  onPermissionsChange: (permissions: RoleMenuPermission[]) => void;
  disabled?: boolean;
}

interface MenuNode extends Menu {
  children: MenuNode[];
  level: number;
}

export function MenuPermissions({ menus, permissions, onPermissionsChange, disabled = false }: MenuPermissionsProps) {
  const [expandedMenus, setExpandedMenus] = React.useState<Set<number>>(new Set());

  // Debug: Monitor props
  React.useEffect(() => {
    // console.log('🔧 MenuPermissions props:', { menusCount: menus.length, permissionsCount: permissions.length, disabled });
  }, [menus, permissions, disabled]);

  // Build hierarchical menu structure
  const buildMenuTree = (menuList: Menu[]): MenuNode[] => {
    const menuMap = new Map<number, MenuNode>();
    const rootMenus: MenuNode[] = [];

    // Create menu nodes
    menuList.forEach(menu => {
      menuMap.set(menu.id, { ...menu, children: [], level: 0 });
    });

    // Build parent-child relationships
    menuList.forEach(menu => {
      const node = menuMap.get(menu.id)!;
      if (menu.parentMenuId) {
        const parent = menuMap.get(menu.parentMenuId);
        if (parent) {
          parent.children.push(node);
          node.level = parent.level + 1;
        }
      } else {
        rootMenus.push(node);
      }
    });

    return rootMenus;
  };

  const menuTree = buildMenuTree(menus);
  // console.log('🔧 Menu tree built:', { menuTreeLength: menuTree.length, menusLength: menus.length });

  const toggleMenuExpansion = (menuId: number) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  const getPermissionForMenu = (menuId: number): RoleMenuPermission => {
    const existing = permissions.find(p => p.menuId === menuId);
    if (existing) {
      return existing;
    }
    
    // Create default permission
    return {
      roleId: 0, // Will be set when saving
      menuId,
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canApprove: false,
      isActive: true
    };
  };

  const updatePermission = (menuId: number, field: keyof RoleMenuPermission, value: boolean) => {
    const newPermissions = [...permissions];
    const existingIndex = newPermissions.findIndex(p => p.menuId === menuId);
    
    if (existingIndex >= 0) {
      newPermissions[existingIndex] = { ...newPermissions[existingIndex], [field]: value };
    } else {
      const newPermission: RoleMenuPermission = {
        roleId: 0,
        menuId,
        canView: field === 'canView' ? value : false,
        canCreate: field === 'canCreate' ? value : false,
        canEdit: field === 'canEdit' ? value : false,
        canDelete: field === 'canDelete' ? value : false,
        canApprove: field === 'canApprove' ? value : false,
        isActive: true
      };
      newPermissions.push(newPermission);
    }

    // Cascade permissions to child menus
    cascadePermissionsToChildren(menuId, field, value, newPermissions);
    
    // Cascade permissions up to parent menus
    cascadePermissionsToParent(menuId, field, value, newPermissions);

    onPermissionsChange(newPermissions);
  };

  // Function to cascade permissions to child menus
  const cascadePermissionsToChildren = (
    parentMenuId: number, 
    field: keyof RoleMenuPermission, 
    value: boolean, 
    permissionsList: RoleMenuPermission[]
  ) => {
    // Find all child menus of the parent
    const findChildMenus = (parentId: number): number[] => {
      const children: number[] = [];
      const findChildrenRecursive = (currentParentId: number) => {
        menus.forEach(menu => {
          if (menu.parentMenuId === currentParentId) {
            children.push(menu.id);
            findChildrenRecursive(menu.id); // Recursively find nested children
          }
        });
      };
      findChildrenRecursive(parentId);
      return children;
    };

    const childMenuIds = findChildMenus(parentMenuId);

    // Update permissions for all child menus
    childMenuIds.forEach(childMenuId => {
      const childPermissionIndex = permissionsList.findIndex(p => p.menuId === childMenuId);
      
      if (childPermissionIndex >= 0) {
        // Update existing permission
        permissionsList[childPermissionIndex] = { 
          ...permissionsList[childPermissionIndex], 
          [field]: value 
        };
      } else {
        // Create new permission for child menu
        const newChildPermission: RoleMenuPermission = {
          roleId: 0,
          menuId: childMenuId,
          canView: field === 'canView' ? value : false,
          canCreate: field === 'canCreate' ? value : false,
          canEdit: field === 'canEdit' ? value : false,
          canDelete: field === 'canDelete' ? value : false,
          canApprove: field === 'canApprove' ? value : false,
          isActive: true
        };
        permissionsList.push(newChildPermission);
      }
    });
    
    if (childMenuIds.length > 0) {
      // console.log(`🟢 Cascading DOWN: Applied ${field} = ${value} to ${childMenuIds.length} child menus of parent ${parentMenuId}`);
    }

    // If this is a View permission change, also handle dependent permissions
    if (field === 'canView' && !value) {
      // If View is unchecked, uncheck all other permissions for this menu and children
      const allAffectedMenus = [parentMenuId, ...childMenuIds];
      allAffectedMenus.forEach(menuId => {
        const permissionIndex = permissionsList.findIndex(p => p.menuId === menuId);
        if (permissionIndex >= 0) {
          permissionsList[permissionIndex] = {
            ...permissionsList[permissionIndex],
            canView: false,
            canCreate: false,
            canEdit: false,
            canDelete: false,
            canApprove: false
          };
        }
      });
    }
  };

  // Function to cascade permissions up to parent menus
  const cascadePermissionsToParent = (
    childMenuId: number, 
    field: keyof RoleMenuPermission, 
    value: boolean, 
    permissionsList: RoleMenuPermission[]
  ) => {
    // Find the parent menu of this child
    const childMenu = menus.find(menu => menu.id === childMenuId);
    if (!childMenu || !childMenu.parentMenuId) return;

    const parentMenuId = childMenu.parentMenuId;
    
    // If child permission is unchecked, check if all siblings are also unchecked
    if (!value) {
      const siblingMenuIds = menus
        .filter(menu => menu.parentMenuId === parentMenuId)
        .map(menu => menu.id);
      
      // Check if all siblings have this permission unchecked
      const allSiblingsUnchecked = siblingMenuIds.every(siblingId => {
        const siblingPermission = permissionsList.find(p => p.menuId === siblingId);
        return !siblingPermission || !siblingPermission[field];
      });
      
      // If all siblings are unchecked, uncheck the parent too
      if (allSiblingsUnchecked) {
        const parentPermissionIndex = permissionsList.findIndex(p => p.menuId === parentMenuId);
        if (parentPermissionIndex >= 0) {
          permissionsList[parentPermissionIndex] = {
            ...permissionsList[parentPermissionIndex],
            [field]: false
          };
        }
        
        // Recursively check parent's parent
        cascadePermissionsToParent(parentMenuId, field, false, permissionsList);
      }
      
      // Also check if parent should be unchecked based on current state
      // If this child is unchecked and parent is checked, we need to evaluate
      const parentPermission = permissionsList.find(p => p.menuId === parentMenuId);
      if (parentPermission && parentPermission[field]) {
        // Check if ANY siblings have this permission checked
        const anySiblingChecked = siblingMenuIds.some(siblingId => {
          const siblingPermission = permissionsList.find(p => p.menuId === siblingId);
          return siblingPermission && siblingPermission[field];
        });
        
        // If no siblings are checked, uncheck the parent
        if (!anySiblingChecked) {
          const parentPermissionIndex = permissionsList.findIndex(p => p.menuId === parentMenuId);
          if (parentPermissionIndex >= 0) {
            permissionsList[parentPermissionIndex] = {
              ...permissionsList[parentPermissionIndex],
              [field]: false
            };
            // console.log(`🔴 Cascading UP: Unchecked ${field} on parent menu ${parentMenuId} because no children are checked`);
          }
          
          // Recursively check parent's parent
          cascadePermissionsToParent(parentMenuId, field, false, permissionsList);
        }
      }
    } else {
      // If child permission is checked, check if parent should also be checked
      const parentPermissionIndex = permissionsList.findIndex(p => p.menuId === parentMenuId);
      if (parentPermissionIndex >= 0) {
        permissionsList[parentPermissionIndex] = {
          ...permissionsList[parentPermissionIndex],
          [field]: true
        };
      }
      
      // Recursively check parent's parent
      cascadePermissionsToParent(parentMenuId, field, true, permissionsList);
    }
  };

  const renderMenuNode = (node: MenuNode, depth: number = 0) => {
    const permission = getPermissionForMenu(node.id);
    const isExpanded = expandedMenus.has(node.id);
    const hasChildren = node.children.length > 0;
    const isParentMenu = node.menuType === 'MENU';
    
    // Check if this menu has any children (for cascading indicator)
    const hasAnyChildren = (menuId: number): boolean => {
      return menus.some(menu => menu.parentMenuId === menuId);
    };
    
    // Get count of child menus for cascading indicator
    const getChildMenuCount = (menuId: number): number => {
      let count = 0;
      const countChildrenRecursive = (currentMenuId: number) => {
        menus.forEach(menu => {
          if (menu.parentMenuId === currentMenuId) {
            count++;
            countChildrenRecursive(menu.id);
          }
        });
      };
      countChildrenRecursive(menuId);
      return count;
    };

    return (
      <div key={node.id} className="space-y-2">
        <div className={`flex items-center space-x-2 p-2 rounded-lg border ${
          isParentMenu ? 'bg-gray-50' : 'bg-white'
        }`}>
                  {/* Expand/Collapse button for parent menus */}
        {hasChildren && (
          <button
            onClick={() => toggleMenuExpansion(node.id)}
            className="p-1 hover:bg-gray-200 rounded"
            disabled={disabled}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        
        {/* Spacer for menus without children */}
        {!hasChildren && (
          <div className="w-6"></div>
        )}
          
          {/* Menu icon and name */}
          <div className="flex items-center space-x-2 flex-1">
            <Shield className="h-4 w-4 text-blue-600" />
            <span className={`font-medium ${isParentMenu ? 'text-gray-900' : 'text-gray-700'}`}>
              {node.displayName}
            </span>
            {node.hasApprovalWorkflow && (
              <Badge variant="outline" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Approval
              </Badge>
            )}
          </div>

          {/* Permission checkboxes */}
          <div className="flex items-center space-x-4">
            {/* View permission */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`view-${node.id}`}
                checked={permission.canView}
                onCheckedChange={(checked) => updatePermission(node.id, 'canView', checked as boolean)}
                disabled={disabled}
              />
              <Label htmlFor={`view-${node.id}`} className="text-xs flex items-center gap-1">
                <Eye className="h-3 w-3" />
                View
                {hasAnyChildren(node.id) && (
                  <Badge variant="outline" className="text-xs ml-1 text-blue-600">
                    Bidirectional cascade ({getChildMenuCount(node.id)} sub-menu{getChildMenuCount(node.id) !== 1 ? 's' : ''})
                  </Badge>
                )}
              </Label>
            </div>

            {/* Create permission */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`create-${node.id}`}
                checked={permission.canCreate}
                onCheckedChange={(checked) => updatePermission(node.id, 'canCreate', checked as boolean)}
                disabled={!permission.canView || disabled}
              />
              <Label htmlFor={`create-${node.id}`} className="text-xs flex items-center gap-1">
                <Plus className="h-3 w-3" />
                Create
              </Label>
            </div>

            {/* Edit permission */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`edit-${node.id}`}
                checked={permission.canEdit}
                onCheckedChange={(checked) => updatePermission(node.id, 'canEdit', checked as boolean)}
                disabled={!permission.canView || disabled}
              />
              <Label htmlFor={`edit-${node.id}`} className="text-xs flex items-center gap-1">
                <Edit className="h-3 w-3" />
                Edit
              </Label>
            </div>

            {/* Delete permission */}
                          <div className="flex items-center space-x-2">
                <Checkbox
                  id={`delete-${node.id}`}
                  checked={permission.canDelete}
                  onCheckedChange={(checked) => updatePermission(node.id, 'canDelete', checked as boolean)}
                  disabled={!permission.canView || disabled}
                />
                <Label htmlFor={`delete-${node.id}`} className="text-xs flex items-center gap-1">
                  <Trash2 className="h-3 w-3" />
                  Delete
                </Label>
              </div>

            {/* Approve permission (only for menus with approval workflow) */}
            {node.hasApprovalWorkflow && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`approve-${node.id}`}
                  checked={permission.canApprove}
                  onCheckedChange={(checked) => updatePermission(node.id, 'canApprove', checked as boolean)}
                  disabled={!permission.canView || disabled}
                />
                <Label htmlFor={`approve-${node.id}`} className="text-xs flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Approve
                </Label>
              </div>
            )}
          </div>
        </div>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && (
          <div className="ml-6 space-y-2">
            {node.children.map(child => renderMenuNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className={disabled ? 'bg-gray-50' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-700" />
          Menu Permissions
          {disabled && (
            <span className="text-sm font-normal text-gray-500 ml-2">(Read Only)</span>
          )}
        </CardTitle>
        <p className="text-sm text-gray-600">
          Configure what actions users with this role can perform on each menu. 
          <span className="text-blue-600 font-medium"> Permissions automatically cascade between parent and child menus.</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {menuTree.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p>No menus available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {menuTree.map(node => renderMenuNode(node))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
